import { Controller } from "@hotwired/stimulus"
import { morphdomIgnore } from "web-ui/morphdom/ignore"
import * as Log from "web-ui/log"

// Use this when you want to wait for a turbo frame to complete its request
// before submitting the form that you add this controller to. An example is an
// passively submitting form in a frame and a button outside of that frame that
// needs to send a request indicating the form is "complete". It must wait for
// any pending passive submissions to occur before its submit in order to ensure
// that the data the user entered is considered. - Aaron, Fri Jun 10 2022

// |--- Passive submission starts
// |
// | |- Click submit on synchronized form (synchronized form cancels the submission)
// | |
// | |
// |-+- Passive submission ends
//   |- Synchronized form resubmits

// Usage example:
//
// = synchronized_button_to "Submit synchronized form",
//     examples_collaborative_form_synchronized_form_path,
//     class: "primary",
//     target_form_id: "collaborative-form-frame"
//

// Connects to data-controller="synchronized-form"
export default class extends Controller {
  static values = {
    targetFormId: String
  }

  connect() {
    this.debug("Connect", { targetFormId: this.targetFormIdValue })
    this.synchronize = this.synchronize.bind(this)

    this.element.addEventListener("submit", this.synchronize, { capture: true })
  }

  disconnect() {
    this.debug("Disconnect")
    this.element.removeEventListener("submit", this.synchronize, { capture: true })

    this.clearInterval()
  }

  clearInterval() {
    if (this.interval) {
      this.debug("Clearing interval")

      this.interval = null
      clearInterval(this.interval)
    }
  }

  synchronize(event) {
    const targetForm = document.getElementById(this.targetFormIdValue)

    if (!targetForm || targetForm.tagName !== "FORM") {
      this.warn(`Could not find form with ID ${this.targetFormId}`)

      return
    }

    this.clearInterval()

    if (this.isFormBusy(targetForm)) {
      this.debug("Suspending submission")

      event.preventDefault()

      const form = event.target
      const submitter = event.submitter

      submitter?.setAttribute("disabled", "")
      morphdomIgnore(this.element, true)

      this.interval = setInterval(() => {
        if (!this.isFormBusy(targetForm)) {
          clearInterval(this.interval)

          submitter?.removeAttribute("disabled")
          morphdomIgnore(this.element, false)

          form.requestSubmit(submitter)
        }
      }, 50)
    } else {
      this.debug("Allowing submission")
    }
  }

  isFormBusy(form) {
    const dynamicFormController = this.application.getControllerForElementAndIdentifier(form, "dynamic-form")

    if (dynamicFormController == null) {
      this.debug("isFormBusy did not find a dynamic form")

      return false
    }

    const updating = dynamicFormController.isUpdating()
    const hasPendingUpdates = dynamicFormController.hasPendingUpdates()
    const busy = updating || hasPendingUpdates

    this.debug("isFormBusy", { busy, updating, hasPendingUpdates })

    return busy
  }

  debug(message, data) {
    Log.debug("Synchronized Form - " + message, data)
  }

  warn(message) {
    console.warn("Synchronized Form - " + message)
  }
}
